import { Box, Grid, GridProps, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";

/**
 * One titled block of the container drawer.
 *
 * The waste specs draw every section the same way — a muted overline over a
 * tinted panel — so this is the only card shape in the drawer, and the fill,
 * fill-history and activity sections render through it rather than each
 * repeating the shell. Tinted rather than white because the drawer's own
 * surface is white; on the specs' page the relationship is the same, just
 * inverted.
 */
export function SectionCard({
  title,
  adornment,
  actions,
  surface = "tinted",
  children,
}: {
  title: string;
  /** Inline content beside the title, e.g. the section's compliance badges. */
  adornment?: ReactNode;
  /**
   * Content pushed to the card's right edge — the section's own controls, or
   * the state badge the specs hang there (the label card's "Auto-generated").
   * Separate from `adornment` because that reads as part of the heading and
   * this reads as separate from it.
   */
  actions?: ReactNode;
  /**
   * Which way round the card sits against its parent — the inversion the note
   * above describes, now expressible.
   *
   * `"tinted"` (default) is the drawer: a grey panel on the drawer's white
   * surface. `"paper"` is a page: a white card on the app's grey canvas. A
   * tinted card on the canvas is grey on grey and effectively invisible, which
   * is what the default did to the accumulation-area pages.
   */
  surface?: "tinted" | "paper";
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        backgroundColor: surface === "paper" ? "background.paper" : "grey50.main",
        borderRadius: 1,
        px: 3,
        pt: 1,
        pb: 3,
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "1px" }}>
            {title}
          </Typography>
          {adornment}
        </Stack>
        {actions}
      </Stack>
      {children}
    </Box>
  );
}

/**
 * One labelled fact inside a section card, on the specs' three-per-row grid.
 *
 * Lives here rather than beside its first caller because every record card in
 * the drawer lays its fields out the same way, and a second copy would be the
 * thing that drifts.
 */
export function FieldValue({
  label,
  children,
  secondary,
  size = { xs: 12, sm: 6, md: 4 },
}: {
  label: string;
  children: ReactNode;
  /** Extra content below the main value — e.g. the reportable-pounds hint under an amount. */
  secondary?: ReactNode;
  /**
   * Grid width. Defaults to the three-up row these cards use inside a
   * full-width section. A card that is itself only half the page wants fewer,
   * wider columns — the accumulation area's Details card runs two, split 2/3
   * and 1/3, so prose sits beside fixed-width facts rather than opposite an
   * equal half of whitespace.
   */
  size?: GridProps["size"];
}) {
  return (
    <Grid size={size}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {/* `div`, not the default `p`: values include block-level children — the
          classification Chip renders a div, and a div inside a p is invalid
          HTML that React reports as a hydration error. */}
      <Typography variant="body2" component="div">
        {children}
      </Typography>
      {secondary}
    </Grid>
  );
}

/**
 * Muted empty state for a section with nothing to show. Unfilled: it sits
 * inside a SectionCard, whose own tint it would otherwise disappear into.
 */
export function EmptySectionState({ message }: { message: string }) {
  return (
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}
